import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { normalizeRole } from "../utils/authRoles";

const AuthContext = createContext(null);

async function readUserRole(user) {
  const userRef = doc(db, "users", user.uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    await setDoc(
      userRef,
      {
        uid: user.uid,
        email: user.email ?? "",
        role: null,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
    return null;
  }

  return normalizeRole(snapshot.data()?.role);
}

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAuthUser(null);
        setLoading(false);
        return;
      }

      try {
        const role = await readUserRole(user);
        setAuthUser({
          uid: user.uid,
          email: user.email,
          role,
        });
      } catch (error) {
        console.error("Failed to read user role", error);
        setAuthUser({
          uid: user.uid,
          email: user.email,
          role: null,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo(
    () => ({
      authUser,
      loading,
      logout: () => signOut(auth),
    }),
    [authUser, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
